import { useLocation } from "react-router";
import { Redirect } from "react-router-dom";
import { AuthenticationService } from "@services/AuthenticationService";
import Button from '@ui-components/Button';
import { useNavigate } from '@services/NavigationContext';
import Input from '@ui-components/Input';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { api } from "@services/apiRequest";
import Alert from "@ui-components/Alert";
import { validateEmail } from "@utils/validator";
import LogoAmmagamma from "@ui-components/layout/LogoAmmagamma";
import HomeContent from "./Home/HomeContent";
import LogoHEAT from "@ui-components/layout/LogoHEAT";
import LogoAccenture from "@ui-components/layout/LogoAccenture";
import LogoCambridge from "@ui-components/layout/LogoCambridge";
import LogoCFI from "@ui-components/layout/LogoCFI";


const LandingPage = () => {
    const { pathname } = useLocation();
    const intl = useIntl();
    const navigate = useNavigate();

    const isAuthenticated = AuthenticationService.isUserAuthenticated();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const sendInvitation = async () => {

        if (!validateEmail(email)) {
            setError("Insert a valid Email");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setSuccess("");
            await api.post("/users/invitation/signup", { email }, { publicRequest: true });
            setSuccess("Invitation sent successfully!");
        } catch (error) {
            setError(intl.formatMessage({ id: "error_send_invitation" }) + ": " +
                intl.formatMessage({ id: error.response.data.detail }));
        } finally {
            setLoading(false);
        }
    };

    if (isAuthenticated) {
        return (
            <Redirect push to={{ pathname: '/home', state: { from: pathname } }} />
        );
    }

    const goToSignUp = (e) => {
        e.preventDefault();
        const target = document.querySelector('#signup');
        const headerHeight = document.querySelector('header').offsetHeight;
        const targetPosition = target.offsetTop - headerHeight - 16;
        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
        });
    };


    return (
        <>

            <Header navigate={navigate} goToSignUp={goToSignUp} />
            <div className="mt-[120px]">

                <HomeContent exploreTemplate={goToSignUp} />

                <div id="signup" className="bg-am-400 py-16">
                    <div className="flex flex-col items-center justify-center mx-auto max-w-screen-lg px-4 text-center text-black">
                        <p className="text-lg mb-8">
                            Sign up to explore the High-risk EU AI Act Toolkit or log in to access your dashboard.
                        </p>
                        <div className="mt-8 mx-auto w-full max-w-md">

                            <div className="bg-white py-7 shadow rounded-3xl px-7">

                                <div className="mt-2 mb-2">
                                    <Button
                                        full
                                        onClick={() => navigate('/login')}
                                    >
                                        Sign in
                                    </Button>
                                    <p className="my-2 text-center text-l lighter-font text-gray-900">
                                        or
                                    </p>
                                    <div>
                                        <Input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>

                                    <Button
                                        full
                                        styleType="white"
                                        onClick={sendInvitation}
                                        submitting={loading}
                                        disabled={email.length < 3 || loading}
                                    >
                                        Receive link to Sign up
                                    </Button>
                                    {error && (
                                        <div className="mt-4">
                                            <Alert slim type="error" title={error} />
                                        </div>
                                    )}
                                    {success && (
                                        <div className="mt-4">
                                            <Alert slim type="success" title={success} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Footer */}
                <Footer />
            </div>
        </>

    );
};

export default LandingPage;

const Footer = () => (
    <footer className="flex flex-col items-center bg-am-600 pt-10 mt-20">
        <div className="flex flex-wrap justify-start w-full mb-10 gap-y-8 gap-x-16 px-10">
            <div className="flex-[2] h-auto w-full sm:w-auto">
                <a
                    href="https://www.cam.ac.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <LogoCambridge width={250} height={80} color="white" />
                </a>
            </div>
            <div className="flex-[2] h-auto w-full sm:w-auto">
                <a
                    href="https://www.lcfi.ac.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <LogoCFI width={300} height={80} color="white" />
                </a>
            </div>
            <div className="flex-[2] h-auto w-full sm:w-auto">
                <a
                    href="https://www.accenture.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <LogoAccenture height={80} color="white" />
                </a>
            </div>
            <div className="flex-[1] h-auto w-full sm:w-auto">
                <a
                    href="https://www.ammagamma.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <LogoAmmagamma height={80} color="white" />
                </a>
            </div>
        </div>
    </footer>

);

function Header({ navigate, goToSignUp }) {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <header className="fixed top-0 left-0 right-0 bg-am-600 shadow-md z-10 px-4 md:px-24">
            <div className="flex items-center justify-between p-1">
                <div
                    className="w-[170px] h-[70px] cursor-pointer"
                    onClick={() =>
                        window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                        })
                    }
                >
                    <LogoHEAT width={170} height={70} color="white" />
                </div>

                <div className="hidden md:flex items-center gap-2 md:gap-8">
                    <Button
                        styleType="white"
                        onClick={() => navigate("/login")}
                    >
                        Sign In
                    </Button>
                    <Button
                        styleType="secondary"
                        className="px-6 py-2"
                        onClick={goToSignUp}
                    >
                        Sign Up
                    </Button>
                </div>

                <button
                    className="md:hidden text-white focus:outline-none"
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        className="w-8 h-8"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 6h16M4 12h16m-7 6h7"
                        />
                    </svg>
                </button>
            </div>

            {menuOpen && (
                <div className="flex flex-col items-center gap-4 mt-2 bg-am-600 text-white p-4 rounded-md md:hidden">
                    <Button
                        styleType="white"
                        className="w-full text-center"
                        onClick={() => {
                            navigate("/login");
                            setMenuOpen(false);
                        }}
                    >
                        Sign In
                    </Button>
                    <Button
                        styleType="secondary"
                        className="w-full text-center"
                        onClick={(e) => {
                            goToSignUp(e);
                        }}
                    >
                        Sign Up
                    </Button>
                </div>
            )}
        </header>
    );
}
